import { Grid, useTheme } from '@mui/material';
import { gridStateActions } from 'app/components/Common/Grid/GridStateManagement/GridState.redux';
import { navigationStickActions } from 'app/components/GlobalNavigationMenu/NavigationStick.redux';
import { DetailsPage } from 'app/layout/pages/DetailsPage';
import { TaskEntity } from 'app/models/Tasks/Tasks';
import { TaskEntityGroupedList } from './TaskEntityGroupedList';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import React, { createRef, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePromiseTracker } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import { taskDetailsReducer, taskDetailsSliceKey } from '../store/Tasks.redux';
import { taskDetailsSaga } from '../store/Tasks.saga';
import { TasksGrid } from './TaskXGrid';
import { selectTaskEntity } from '../store/Tasks.selectors';
import {
  convertArrayToKeyValuePairs,
  convertSelectedFilterValue,
  formatDateValue,
  formatFilterValues,
  formatSelectedFilter,
  setFormValues,
  TaskSearchFilterName,
} from './TaskFilters/TaskFilterUtilitis';
import { TaskSearchChips } from './TaskFilters/TaskSearchChips';
import { TaskSearchFilters } from './TaskFilters/TaskSearchFilters';
import { selectUser } from 'oidc/user.selectors';
import { defaultTaskFilters } from './TaskFilters/ITaskFilter';
import { placementStatusReducer, placementStatusSliceKey } from 'store/redux-store/placement-status/slice';
import { placementDetailSliceKey } from 'store/redux-store/placement-details/slice';
import { placementDetailsSaga } from 'store/redux-store/placement-details/saga';
import useDocumentTitle from 'utils/customHooks/useDocumentTitle';
import { trackPageView } from 'app-insights/appInsightsTracking';
import { pageNames } from 'app/constants/PageNames';
import { TaskTitleActionWrapper } from './TaskTitleActionWrapper';
import { TaskResultsChips } from './TaskResultsChip';
import { RootState } from 'types';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { ModsFormBanner } from '@AMIEWEB/Placement/PlacementDetails/PlacementTabPanel/ModificationsTab/CommonComponents/DialogComponents/ModsFormBanner';
import { DrawerFitted } from '@AMIEWEB/Common/Drawer/Drawer';
import { globalSearchActions } from 'store/redux-store/global-search/slice';
import { selectSearchResults } from 'store/redux-store/global-search/selectors';
import { CommentDrawerWrapper } from './TaskDrawer/CommentDrawerWrapper';

export const TaskManagement = ({
  isLinkTaskMod = false,
  foundPlacement,
  isExpanded,
  setFiltersExpanded,
  setChipExpanded,
}: {
  isLinkTaskMod?: boolean;
  foundPlacement?: any;
  isExpanded?: boolean;
  setFiltersExpanded?: any;
  setChipExpanded?: any;
}) => {
  useInjectSaga({ key: taskDetailsSliceKey, saga: taskDetailsSaga });
  useInjectReducer({ key: taskDetailsSliceKey, reducer: taskDetailsReducer });
  useInjectReducer({ key: placementStatusSliceKey, reducer: placementStatusReducer });
  useInjectSaga({ key: placementDetailSliceKey, saga: placementDetailsSaga });
  useDocumentTitle({ title: pageNames.taskInbox });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { drawerData } = useSelector(selectSearchResults);
  const hiddenSubmitButton = createRef<HTMLButtonElement>();
  const { promiseInProgress: isCallingAPI } = usePromiseTracker({ area: 'get-task-list', delay: 0 });
  const taskEntity = useSelector(selectTaskEntity);
  const GRID_TAG = 'GlobalSearchGrid';
  const gridTag = `${GRID_TAG}-${SearchType.tasks}`;
  const userInfo = useSelector(selectUser).userInfo;
  const filterPreferences = useSelector((state: RootState) => {
    return state.userPreferenceData.userGridPreferences.find(item => item.id === 'GlobalSearchGrid-tasks');
  });
  const [gridErrorMessage, setGridErrorMessage] = useState('');

  useEffect(() => {
    trackPageView({ name: pageNames.taskInbox });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const absoluteDefaultValues = {
    [TaskSearchFilterName.assignedTo]: [
      {
        id: userInfo?.employeeId,
        value: userInfo?.employeeId,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      },
    ],
    [TaskSearchFilterName.taskCategory]: [],
    [TaskSearchFilterName.taskName]: [],
    [TaskSearchFilterName.status]: [
      {
        id: 1,
        name: 'New',
        value: 1,
      },
      {
        id: 2,
        name: 'Pending',
        value: 2,
      },
    ],
    [TaskSearchFilterName.dueDate]: null,
    [TaskSearchFilterName.candidateName]: [],
    [TaskSearchFilterName.candidateId]: isLinkTaskMod
      ? [
          {
            id: foundPlacement?.header?.candidate?.id,
            value: foundPlacement?.header?.candidate?.id,
            name: `${foundPlacement?.header?.candidate?.id}`,
          },
        ]
      : [],
    [TaskSearchFilterName.placementId]: isLinkTaskMod
      ? [
          {
            id: foundPlacement.header.placementId,
            value: foundPlacement.header.placementId,
            name: `${foundPlacement.header.placementId}`,
          },
        ]
      : [],
    [TaskSearchFilterName.orderId]: isLinkTaskMod
      ? [
          {
            id: foundPlacement.header.orderId,
            value: foundPlacement.header.orderId,
            name: `${foundPlacement.header.orderId}`,
          },
        ]
      : [],
    [TaskSearchFilterName.facility]: [],
    [TaskSearchFilterName.facilityId]: isLinkTaskMod
      ? [
          {
            id: foundPlacement.header.facility.id,
            value: foundPlacement.header.facility.id,
            name: `${foundPlacement.header.facility.id}`,
          },
        ]
      : [],
    [TaskSearchFilterName.createdBy]: [],
  };

  const onApplyFilter = values => {
    const formattedValue = formatFilterValues(values);
    dispatch(gridStateActions.setSelectedFilters(formattedValue));
  };

  const applyDefaultValues = () => {
    setFormValues(absoluteDefaultValues, setValue, absoluteDefaultValues?.dueDate);

    dispatch(
      gridStateActions.setFilterAttributes({
        filterObject: Object.entries(absoluteDefaultValues),
        selectedChipFilters: {
          ...defaultTaskFilters,
          assignedToIds: [userInfo?.employeeId],
          candidateIds: isLinkTaskMod ? [foundPlacement?.header?.candidate?.id] : [],
          placementIds: isLinkTaskMod ? [foundPlacement.header.placementId] : [],
          orderIds: isLinkTaskMod ? [foundPlacement.header.orderId] : [],
          facilityIds: isLinkTaskMod ? [foundPlacement.header.facility.id] : [],
        },
        associatedGridName: gridTag,
      }),
    );
  };

  useEffect(() => {
    dispatch(navigationStickActions.setSelectedMenu(undefined));
    if (filterPreferences?.value && !isLinkTaskMod) {
      const selectedFilterName = filterPreferences?.value?.selectedFilter?.name;
      const filterSet = filterPreferences?.value?.filterAttributes?.filterSet?.find(
        item => item.name === selectedFilterName,
      );
      if (filterSet) {
        const selectedFilter = {
          name: filterSet?.name,
          value: filterSet?.value,
          isDefault: filterSet?.isDefault,
        };
        const selectedFilterValue = formatSelectedFilter(selectedFilter?.value);
        const selectedFilters = convertArrayToKeyValuePairs(selectedFilterValue);
        let DefaultDateValue: any = null;
        if (selectedFilters?.dueDate) {
          DefaultDateValue = formatDateValue(selectedFilters);
        }
        setFormValues(selectedFilters, setValue, DefaultDateValue);

        const selectedFilterChips = convertSelectedFilterValue(selectedFilter?.value);
        dispatch(
          gridStateActions?.setFilterAttributes({
            filterObject: selectedFilterValue,
            selectedChipFilters: selectedFilterChips,
            associatedGridName: gridTag,
          }),
        );
      } else {
        applyDefaultValues();
      }
    } else {
      applyDefaultValues();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPreferences?.value?.selectedFilter?.name]);

  React.useEffect(() => {
    return () => {
      dispatch(gridStateActions.reset());
      dispatch(globalActions.closeBanner());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formMethods = useForm({ defaultValues: absoluteDefaultValues });
  const { setValue, handleSubmit } = formMethods;

  const chipsData = useMemo(
    () => <TaskResultsChips onApplyFilter={onApplyFilter} hideDelete={true} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(filter => onApplyFilter(filter))}>
          {isLinkTaskMod && gridErrorMessage && (
            <Grid item xs={12}>
              <ModsFormBanner message={gridErrorMessage ?? ''} />
            </Grid>
          )}
          <DetailsPage
            title={!isLinkTaskMod ? t('notification.taskManagement.title') : ''}
            enablePageActionSpace
            titleActions={
              !isLinkTaskMod ? (
                <TaskTitleActionWrapper
                  category={SearchType.tasks}
                  chips={chipsData}
                  defaultValues={absoluteDefaultValues}
                />
              ) : null
            }
            filters={
              <TaskSearchFilters
                defaultValues={absoluteDefaultValues}
                isLinkTaskMod={isLinkTaskMod}
                setFiltersExpanded={setFiltersExpanded}
                onApplyFilter={onApplyFilter}
              />
            }
            chips={
              <TaskSearchChips onApplyFilter={onApplyFilter} hideDelete={false} setChipExpanded={setChipExpanded} />
            }
            stickIconArray={[4]}
            isLinkTaskMod={isLinkTaskMod}
          >
            <Grid container spacing={2}>
              {taskEntity !== TaskEntity.ALL && !isLinkTaskMod && (
                <Grid item xs={3}>
                  <TaskEntityGroupedList />
                </Grid>
              )}
              <Grid item xs={taskEntity !== TaskEntity.ALL ? 9 : 12}>
                <TasksGrid
                  loading={isCallingAPI}
                  gridTag={gridTag}
                  isLinkTaskMod={isLinkTaskMod}
                  setGridErrorMessage={setGridErrorMessage}
                  isExpanded={isExpanded}
                  gridErrorMessage={gridErrorMessage}
                />
              </Grid>
            </Grid>
            <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
          </DetailsPage>
        </form>
      </FormProvider>
      {
        <DrawerFitted
          onDrawerClose={() => {
            dispatch(globalSearchActions.setDrawerData({ open: false, data: undefined }));
          }}
          width={450}
          top={0}
          backgroundColor={theme.palette.framework.system.whisper}
          open={drawerData?.open ?? false}
        >
          {drawerData?.open && drawerData?.data ? <CommentDrawerWrapper /> : null}
        </DrawerFitted>
      }
    </>
  );
};
